'use client';

import { c as _c } from "react/compiler-runtime";
import { Xmark } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useContext, useEffect, useRef, useState } from 'react';
import { Dialog as AriaDialog, Modal, ModalOverlay, type ModalOverlayProps, OverlayTriggerStateContext } from 'react-aria-components';
import { Button } from '~/components/Button';
import styles from './Dialog.module.css';
type DialogProps = ModalOverlayProps & {
  children: React.ReactNode;
  hasPadding?: boolean;
  variant?: 'default' | 'popup';
};
type DialogHeaderProps = DialogContentProps & {
  showCloseButton?: boolean;
  variant?: 'default' | 'notification';
};
type DialogContentProps = {
  children?: React.ReactNode;
  className?: string;
  hasPadding?: boolean;
};
type DialogScrollableContentProps = DialogContentProps;
function CloseButton() {
  const $ = _c(8);
  const state = useContext(OverlayTriggerStateContext);
  const t = useTranslations("aria");
  let t0;
  if ($[0] !== state) {
    t0 = () => state?.close();
    $[0] = state;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("close");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Xmark size="large" decorative={true} />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1) {
    t3 = <Button onPress={t0} ariaLabel={t1} variant="icon">{t2}</Button>;
    $[5] = t0;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export const DialogHeader = t0 => {
  const $ = _c(15);
  const {
    children,
    showCloseButton: t1,
    className,
    hasPadding: t2,
    variant: t3
  } = t0;
  const showCloseButton = t1 === undefined ? true : t1;
  const hasPadding = t2 === undefined ? true : t2;
  const variant = t3 === undefined ? "default" : t3;
  const t4 = variant === "notification";
  const t5 = variant === "notification";
  const t6 = variant === "notification";
  let t7;
  if ($[0] !== className || $[1] !== hasPadding || $[2] !== t4 || $[3] !== t5 || $[4] !== t6) {
    t7 = clsx(styles.header, className, {
      [styles.headerPadding]: hasPadding,
      [styles.noBorder]: t4,
      [styles.notification]: t5,
      [styles.smPadding]: t6
    });
    $[0] = className;
    $[1] = hasPadding;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = clsx(styles.headerContent);
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== children) {
    t9 = <div className={t8}>{children}</div>;
    $[7] = children;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== showCloseButton) {
    t10 = showCloseButton && <CloseButton />;
    $[9] = showCloseButton;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t10 || $[12] !== t7 || $[13] !== t9) {
    t11 = <div className={t7}>{t9}{t10}</div>;
    $[11] = t10;
    $[12] = t7;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  return t11;
};
export const DialogContent = t0 => {
  const $ = _c(6);
  const {
    children,
    className,
    hasPadding: t1
  } = t0;
  const hasPadding = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== className || $[1] !== hasPadding) {
    t2 = clsx(styles.content, className, {
      [styles.contentPadding]: hasPadding
    });
    $[0] = className;
    $[1] = hasPadding;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[3] = children;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
export const DialogScrollableContent = t0 => {
  const $ = _c(9);
  const {
    children,
    className,
    hasPadding: t1
  } = t0;
  const hasPadding = t1 === undefined ? true : t1;
  const contentRef = useRef(null);
  const [hasScroll, setHasScroll] = useState(false);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const contentElement = contentRef.current;
      if (!contentElement) {
        return;
      }
      const handleScroll = () => {
        const hasScrolled = contentElement.scrollTop > 0;
        setHasScroll(hasScrolled);
      };
      handleScroll();
      contentElement.addEventListener("scroll", handleScroll);
      return () => contentElement.removeEventListener("scroll", handleScroll);
    };
    t3 = [setHasScroll];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== className || $[3] !== hasPadding) {
    t4 = clsx(styles.content, styles.scrollable, className, {
      [styles.contentPadding]: hasPadding
    });
    $[2] = className;
    $[3] = hasPadding;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = hasScroll || undefined;
  let t6;
  if ($[5] !== children || $[6] !== t4 || $[7] !== t5) {
    t6 = <div ref={contentRef} className={t4} data-scroll={t5}>{children}</div>;
    $[5] = children;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};
export const Dialog = t0 => {
  const $ = _c(24);
  let children;
  let className;
  let isOpen;
  let onOpenChange;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      children,
      isOpen,
      onOpenChange,
      className,
      hasPadding: t1,
      variant: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = isOpen;
    $[4] = onOpenChange;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    children = $[1];
    className = $[2];
    isOpen = $[3];
    onOpenChange = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const hasPadding = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "default" : t2;
  const t3 = variant === "popup";
  let t4;
  if ($[8] !== t3) {
    t4 = clsx(styles.modal, {
      [styles.popup]: t3
    });
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== className || $[11] !== hasPadding) {
    t5 = clsx(styles.dialog, className, {
      [styles.dialogPadding]: hasPadding
    });
    $[10] = className;
    $[11] = hasPadding;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== children || $[14] !== t5) {
    t6 = <AriaDialog className={t5} role="dialog">{children}</AriaDialog>;
    $[13] = children;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t4 || $[17] !== t6) {
    t7 = <Modal className={t4} data-test-id="DialogModal">{t6}</Modal>;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== isOpen || $[20] !== onOpenChange || $[21] !== props || $[22] !== t7) {
    t8 = <ModalOverlay isDismissable={true} isOpen={isOpen} onOpenChange={onOpenChange} className={styles.overlay} data-test-id="DialogModalOverlay" {...props}>{t7}</ModalOverlay>;
    $[19] = isOpen;
    $[20] = onOpenChange;
    $[21] = props;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};